import { FC, memo, useState } from 'react'
import styled, { css } from 'styled-components'
import { useTranslation } from 'react-i18next'

import {
  Button,
  Icon,
  IconColor,
  IconSize,
  IconTypes,
  Loader,
  Modal,
} from '@app/ui'
import { ButtonSize, ButtonType, ModalType } from '@app/types'
import { AuthHelper } from '@app/helpers'
import { useSnapshot } from 'valtio'
import { CurrentUserState } from '@app/storage'
import { useTourStage } from '@app/helpers/hooks'
import { PosthogEvent } from '@app/constants'
import { captureAnalytics } from '@app/helpers/misc'

interface ShareSocialProps {
  open: boolean;
  link: string;
  loading?: boolean;
  onClose: () => void;
  channelName?: string;
}

const ShareSocial: FC<ShareSocialProps> = ({
  open,
  link,
  loading,
  onClose,
  channelName
}) => {
  const { t } = useTranslation()
  const [ copied, setCopied ] = useState(false)

  const hasShareLink = Boolean(link)
  const shareLinks = AuthHelper.shareLinks(link)

  const handleAnalytics = (shareMethod) => {
    captureAnalytics(PosthogEvent.SHARE_CHANNEL_INVITATION_LINK, {
      channel_name: channelName,
      share_method: shareMethod
    })
  }
  const copyText = async () => {
    if (!hasShareLink) return

    navigator?.clipboard?.writeText(link)
    onClose()
    setShowUserInvite(true)
    setCopied(true)
    handleAnalytics('Copy to clipboard')
  }
  const {
    isTouring,
    currentTourStage,
    tourStage,
  } = useSnapshot(CurrentUserState)
  const [ _currentTourStage, setTourStage ] = useTourStage(currentTourStage)
  const [ showUserInvite, setShowUserInvite ] = useState(false)

  const handleSocialClick =(social)=>{
    if (!hasShareLink) return

    handleAnalytics(social.name)
    window.open(social.link, '_blank')

  }

  const socialsComponents = Object.values(shareLinks).map((social, index) => (
    <StyledGridItem
      className={index === 0 ? 'first-row' : ''}
      key={social.name}
      $disabled={!hasShareLink}
      onClick={() => handleSocialClick(social)}
    >
      <a className="link" href={isTouring &&
        currentTourStage=='userInviteMenu' && social.link} target="_blank">
        <Icon type={social.iconType}
          size={IconSize.xLarge}
          color={(isTouring && currentTourStage=='userInviteMenu'? '#616C78': '#616C78') as IconColor} />
      </a>
      <p className={`social-title ${isTouring && currentTourStage=='userInviteMenu' && 'disabled'}`}>{social.name}</p>
    </StyledGridItem>
  ))

  const showModal = () => {
    return (
      <StyledModal open={open} type={ModalType.standard} onClose={onClose}>
        {loading ? (
          <Loader />
        ) : (
          <>
            <>
              <StyledCaption>{t('social.shareOn')}</StyledCaption>
              <StyledGrid>
                {socialsComponents}
                <StyledGridItem $disabled={!hasShareLink}>
                  <div onClick={copyText}>
                    <Icon
                      type={IconTypes.copy}
                      size={IconSize.xLarge}
                      color={IconColor.grayDarker}
                    />
                  </div>
                  <p className="social-title">{t('social.copyToClipboard')}</p>
                </StyledGridItem>
              </StyledGrid>
            </>
          </>
        )}
      </StyledModal>
    )
  }

  return (
    <div>
      {showModal()}
      <ShowPopUp
        open={copied}
        onClose={() => {
          setCopied(false)
        }}
      />
    </div>
  )
}

interface ShowPopuprops {
  open: boolean;
  onClose: () => void;
}
const ShowPopUp: FC<ShowPopuprops> = ({ open, onClose }) => {
  const { t } = useTranslation()
  return (
    <CopiedModal open={open} type={ModalType.standard} onClose={onClose}>
      <CopiedCard>
        <CopiedIcon>
          <Icon
            type={IconTypes.checkmark}
            color={IconColor.white}
            width={28}
            height={28}
          />
        </CopiedIcon>
        <CopiedTitle>{t('shareSocial.success').toUpperCase()}</CopiedTitle>
        <CopiedDescription>{t('link.copied')}</CopiedDescription>
        <CopiedButtonContainer>
          <Button
            size={ButtonSize.normal}
            type={ButtonType.primary}
            fluid
            onClick={onClose}
          >
            <span className="button-text">{t('common.ok').toUpperCase()}</span>
          </Button>
        </CopiedButtonContainer>
      </CopiedCard>
    </CopiedModal>
  )
}

const WelcomeAppContentStyles = css`
  .note {
    color: #92979F;
    text-align: center;
    font-size: 11px;
    font-weight: 400;
    width: 100%;
  }
  .content .sub {
    color: #616C78;
    text-align: center;
    font-size: 14px;
    font-weight: 700;
    margin-bottom: 8px;
    width: 100%;
  }
  .note span {
    font-weight: 700;
  }
  .content .sub span{
    color: #363B41;
    font-size: 15px;
    font-weight: 700;
  }
  .content .cta {
    color: #48C0AE;
    text-align: center;
    font-size: 14px;
    font-weight: 700;
    width: 100%;
    margin: 32px 0;
  }
  .content button {
    display: inline-block;
    width: 80%;
    margin-left: 10%;
    margin-bottom: 16px;
    .icon, .icon-text {
      display: inline-block;
      padding: 0 2.5px;
      vertical-align: middle;
      color: #FFF;
      font-size: 14px;
      font-weight: 500;
    }
    .icon {
      position: relative;
      transform: translateY(3px);
    }
  }
  .content .body {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-top: 16px;
    padding-left: 5%;
  }
  .body .caption {
    color: #363B41;
    font-size: 14px;
    font-weight: 500;
    height: 18px;
    line-height: 15px;
  }
  .content .title  {
    color: #2B2D30;
    text-align: center;
    font-size: 20px;
    font-weight: 700;
    margin-bottom: 6px;
    margin-top: 16px;
    width: 100%;
  }
`

const StyledModal = styled(Modal).attrs({ zIndex: 10001 })``
const StyledCaption = styled.p`
  width: 100%;
  text-align: left;
  color: var(--color-gray-darker);
`
const StyledCaption2 = styled.p`
  width: 100%;
  text-align: center;
  color: var(--color-black);
  font-weight: bold;
  font-size: var(--font-size-small);
`

const StyledGrid = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  padding: 25px 0;
`

const StyledGridItem = styled.div<{ $disabled?: boolean }>`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  cursor: ${({ $disabled }) => ($disabled ? 'default' : 'pointer')};
  opacity: ${({ $disabled }) => ($disabled ? 0.45 : 1)};
  pointer-events: ${({ $disabled }) => ($disabled ? 'none' : 'auto')};

  &.first-row {
    margin-bottom: 32px;
  }
  .social-title {
    margin-top: 14px;
    line-height: 14px;
    color: var(--font-primary);
    text-align: center;
  }
  .social-title-bold {
    margin-top: 16px;
    font-size: var(--font-size-medium);
    line-height: 14px;
    color: var(--font-primary);
    text-align: center;
  }
  .link {
    line-height: 0;
  }
  .social-title.disabled {
    color: #616C78;
  }
`
const StyledButtonContainer = styled.div`
  width: 60%;
  margin-top: 16px;
  display: flex;
  justify-content: stretch;
  padding: 0 5%;

  .button-text {
    font-size: var(--font-size-normal);
    font-weight: bold;
  }
`

const CopiedModal = styled(Modal).attrs({ zIndex: 10002 })`
  .modal-content.type-standard {
    padding: 0 !important;
    width: calc(100% - 48px);
    max-width: 345px;
    border-radius: 12px;
    overflow: hidden;
    background: var(--zinc-900, #18181B);
    box-shadow: 0 24px 48px rgba(0, 0, 0, 0.42);
  }
`

const CopiedCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  position: relative;
  min-height: 248px;
  box-sizing: border-box;
  padding: 28px 20px 16px;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: -285px;
    left: 50%;
    transform: translateX(-50%);
    width: 460px;
    height: 460px;
    border-radius: 50%;
    pointer-events: none;
    background:
      radial-gradient(
        circle,
        rgba(var(--accent-light-rgb), 0.72) 0%,
        rgba(var(--accent-rgb), 0.42) 24%,
        rgba(var(--accent-deep-rgb), 0.16) 50%,
        transparent 74%
      );
    filter: blur(32px);
  }

  > * {
    position: relative;
    z-index: 1;
  }
`

const CopiedIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 60px;
  height: 60px;
  margin-bottom: 10px;
  border-radius: 50%;
  background: var(--bg-nonary);
  filter: drop-shadow(0 0 32px rgba(var(--accent-rgb), 0.5));
`

const CopiedTitle = styled.h2`
  color: #FAFAFA;
  font-size: 20px;
  font-weight: 600;
  line-height: 28px;
  margin: 0 0 10px;
  text-transform: none;
`

const CopiedDescription = styled.p`
  color: #D4D4D8;
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
  margin: 0;
`

const CopiedButtonContainer = styled.div`
  width: 100%;
  margin-top: auto;

  .Button,
  button {
    height: 48px;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
  }

  .button-text {
    font-size: 14px;
    font-weight: bold;
  }
`

export default memo(ShareSocial)
